import { LabeledInfo } from "./LabeledInfo";
import {
  AddOutlined,
  DeleteOutlineOutlined,
  ExpandMoreOutlined,
  MoveToInboxOutlined,
  PictureAsPdfOutlined,
  SearchOutlined,
  VisibilityOutlined,
  UndoOutlined
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { SourceTrace } from "./SourceTrace";
import {
  Asn,
  AsnLine,
  ProductItem,
  Receipt,
  addReceiptLine,
  addAsnLine,
  changeAsnStatus,
  claimAsnCustomsClearance,
  completeAsnCustomsClearance,
  completeReceipt,
  createAdHocReceipt,
  createAsn,
  createReceipt,
  deleteReceiptLine,
  generateReceiptStock,
  isAuthenticationError,
  loadAsn,
  loadAsnAudit,
  loadAsnLines,
  loadAsns,
  loadAdHocReceipts,
  loadDeliveryClients,
  loadDocumentTemplates,
  loadGeneratedAsnDocumentContent,
  loadGeneratedAsnDocuments,
  loadInboundAsnDocumentData,
  loadNumberingPolicies,
  loadPackaging,
  loadPurchaseOrderSuggestion,
  loadProducts,
  loadProcessPoints,
  loadReceiptStock,
  loadReceiptLines,
  loadReceipts,
  loadSuppliers,
  loadWarehouseStructure,
  saveGeneratedAsnDocument,
  reopenReceipt
} from "./api";
import { generateDocumentPdf } from "./documentGeneration";
import { login } from "./auth";
import { PageHeader } from "./PageHeader";
import { MobileActionBar } from "./MobileActionBar";
import { MobileOperationalHeader } from "./MobileOperationalHeader";
import { ResponsiveStatusTabs } from "./ResponsiveStatusTabs";
import { ListRowActions } from "./ListRowActions";
import {
  compareOptionalDates,
  matchesListSyntax
} from "./listSearch";
import {
  canStartAsnReceipt,
  startReceiptError
} from "./inboundUiState";
import { operationalStatusColor, operationalStatusLabel } from "./operationalStatus";
import { setDiagnosticContext } from "./terminalDiagnostics";
import i18n from "./i18n";

const organizationId = "10000000-0000-0000-0000-000000000001";
const statusColors: Record<Asn["status"], "default" | "info" | "warning" | "success"> = {
  DRAFT: "default",
  EXPECTED: "info",
  CUSTOMS_PENDING: "warning",
  ARRIVED: "warning",
  RECEIVING: "info",
  RECEIVED: "success",
  CLOSED: "success",
  CANCELLED: "default"
};
const closedStatuses = new Set<Asn["status"]>([
  "CLOSED", "CANCELLED"
]);

function receiptStatusLabel(status: Receipt["status"]) {
  return i18n.t(`inbound.receiptStatus.${status}`);
}

function matchesSyntax(asn: Asn, expression: string) {
  const fields: Record<string, string> = {
    id: `${asn.id} ${asn.asnNumber} ${asn.externalReference ?? ""}`,
    fournisseur: `${asn.supplierCode} ${asn.supplierName}`,
    supplier: `${asn.supplierCode} ${asn.supplierName}`,
    client: `${asn.clientCode ?? ""} ${asn.clientName ?? ""}`,
    entrepot: asn.warehouseCode,
    warehouse: asn.warehouseCode,
    statut: `${asn.status} ${operationalStatusLabel(asn.status)}`,
    status: asn.status,
    po: asn.purchaseOrderNumber ?? "",
    reception: asn.receiptNumbers ?? "",
    hu: asn.handlingUnitNumbers ?? "",
    douane: asn.customsClearanceNumber ?? "",
    date: `${asn.createdAt} ${asn.updatedAt} ${asn.expectedArrivalAt ?? ""}`
  };
  return matchesListSyntax(fields, expression);
}

function matchesReceiptSyntax(
  receipt: Receipt,
  warehouseCode: string,
  expression: string
) {
  const fields: Record<string, string> = {
    id: `${receipt.id} ${receipt.receiptNumber}`,
    fournisseur: `${receipt.supplierCode} ${receipt.supplierName}`,
    supplier: `${receipt.supplierCode} ${receipt.supplierName}`,
    entrepot: warehouseCode,
    warehouse: warehouseCode,
    statut: `${receipt.status} ${receiptStatusLabel(receipt.status)}`,
    status: receipt.status,
    po: "",
    date: `${receipt.startedAt} ${receipt.completedAt ?? ""}`
  };
  return matchesListSyntax(fields, expression);
}

export function InboundAsnPage({
  onBack,
  initialSearch = "",
  initialTargetId = null,
  initialOpenDetail = false
}: {
  onBack: () => void;
  initialSearch?: string;
  initialTargetId?: string | null;
  initialOpenDetail?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [asnOpen, setAsnOpen] = useState(false);
  const [adHocOpen, setAdHocOpen] = useState(false);
  const [activeAdHocReceipt, setActiveAdHocReceipt] =
    useState<Receipt | null>(null);
  const [selected, setSelected] = useState<Asn | null>(null);
  const [expandedAsnIds, setExpandedAsnIds] = useState<string[]>([]);
  const [expandedAdHocReceiptIds, setExpandedAdHocReceiptIds] = useState<string[]>([]);
  const [asnNumber, setAsnNumber] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [stockOwnerId, setStockOwnerId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [purchaseOrder, setPurchaseOrder] = useState("");
  const [poSuggestionId, setPoSuggestionId] = useState("");
  const [suggestedItemId, setSuggestedItemId] = useState("");
  const [suggestedQuantity, setSuggestedQuantity] = useState("");
  const [suggestedUom, setSuggestedUom] = useState("EA");
  const [externalReference, setExternalReference] = useState("");
  const [expectedArrival, setExpectedArrival] = useState("");
  const [customsClearanceRequired, setCustomsClearanceRequired] = useState(false);
  const [listTab, setListTab] = useState<"open" | "closed">("open");
  const [sortDate, setSortDate] =
    useState<"createdAt" | "expectedArrivalAt">("createdAt");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [syntaxSearch, setSyntaxSearch] = useState("");
  const [mobileSelection, setMobileSelection] = useState<{
    kind: "asn" | "receipt";
    id: string;
  } | null>(null);
  const [adHocReceiptNumber, setAdHocReceiptNumber] = useState("");
  const [adHocSupplierId, setAdHocSupplierId] = useState("");
  const [adHocStockOwnerId, setAdHocStockOwnerId] = useState("");
  const [adHocWarehouseId, setAdHocWarehouseId] = useState("");
  const [adHocReasonCode, setAdHocReasonCode] = useState("UNPLANNED_DELIVERY");
  const [adHocReasonComment, setAdHocReasonComment] = useState("");

  const asns = useQuery({ queryKey: ["asns"], queryFn: loadAsns });
  const adHocReceipts = useQuery({
    queryKey: ["ad-hoc-receipts"], queryFn: loadAdHocReceipts
  });
  const suppliers = useQuery({ queryKey: ["suppliers"], queryFn: loadSuppliers });
  const asnProducts = useQuery({ queryKey: ["products"], queryFn: loadProducts });
  const stockOwners = useQuery({
    queryKey: ["delivery-clients"],
    queryFn: loadDeliveryClients
  });
  const structure = useQuery({
    queryKey: ["warehouse-structure"], queryFn: loadWarehouseStructure
  });
  const numbering = useQuery({
    queryKey: ["numbering-policies"], queryFn: loadNumberingPolicies
  });
  const asnNumbering = numbering.data?.find(
    (policy) => policy.documentType === "ASN"
  );
  const receiptNumbering = numbering.data?.find(
    (policy) => policy.documentType === "RECEIPT"
  );
  const numberingUnavailable = numbering.isError
    || (!numbering.isPending && !receiptNumbering);
  const warehouses = structure.data?.filter((node) => node.type === "WAREHOUSE") ?? [];
  useEffect(() => {
    if (!initialSearch) return;
    setSyntaxSearch(initialSearch);
    const matchingAsn = asns.data?.find((asn) =>
      asn.asnNumber.toLowerCase() === initialSearch.toLowerCase()
    );
    if (matchingAsn) {
      setListTab(closedStatuses.has(matchingAsn.status) ? "closed" : "open");
      setExpandedAsnIds((current) => current.includes(matchingAsn.id)
        ? current : [...current, matchingAsn.id]);
    }
  }, [asns.data, initialSearch]);
  useEffect(() => {
    if (!initialTargetId || !asns.data) return;
    const matchingAsn = asns.data.find((asn) => asn.id === initialTargetId);
    if (!matchingAsn) return;
    setListTab(closedStatuses.has(matchingAsn.status) ? "closed" : "open");
    setSyntaxSearch(matchingAsn.asnNumber);
    if (initialOpenDetail) {
      setExpandedAsnIds((current) => current.includes(matchingAsn.id)
        ? current : [...current, matchingAsn.id]);
    }
    window.setTimeout(() => document.getElementById(
      `asn-${matchingAsn.id}`
    )?.scrollIntoView({ behavior: "smooth", block: "center" }), 0);
  }, [asns.data, initialOpenDetail, initialTargetId]);
  const warehouseCodes = useMemo(
    () => new Map(warehouses.map((warehouse) => [warehouse.id, warehouse.code])),
    [warehouses]
  );
  const filteredAsns = useMemo(() => {
    const expectedClosed = listTab === "closed";
    return [...(asns.data ?? [])].filter((asn) =>
      closedStatuses.has(asn.status) === expectedClosed
      && (!syntaxSearch.trim() || matchesSyntax(asn, syntaxSearch))
    ).sort((left, right) => {
      const leftValue = sortDate === "createdAt"
        ? left.createdAt : left.expectedArrivalAt;
      const rightValue = sortDate === "createdAt"
        ? right.createdAt : right.expectedArrivalAt;
      return compareOptionalDates(leftValue, rightValue, sortDirection);
    });
  }, [
    asns.data, listTab, sortDate, sortDirection, syntaxSearch, i18n.resolvedLanguage
  ]);
  const filteredAdHocReceipts = useMemo(() => {
    const expectedClosed = listTab === "closed";
    return [...(adHocReceipts.data ?? [])].filter((receipt) =>
      (!["OPEN", "POSTING"].includes(receipt.status)) === expectedClosed
      && (!syntaxSearch.trim() || matchesReceiptSyntax(
        receipt,
        warehouseCodes.get(receipt.warehouseId) ?? "",
        syntaxSearch
      ))
    ).sort((left, right) => compareOptionalDates(
      sortDate === "createdAt"
        ? left.startedAt : left.completedAt ?? left.startedAt,
      sortDate === "createdAt"
        ? right.startedAt : right.completedAt ?? right.startedAt,
      sortDirection
    ));
  }, [
    adHocReceipts.data, listTab, sortDate, sortDirection, syntaxSearch, warehouseCodes, i18n.resolvedLanguage
  ]);
  const openAsnCount = asns.data?.filter(
    (asn) => !closedStatuses.has(asn.status)
  ).length ?? 0;
  const openAdHocCount = adHocReceipts.data?.filter(
    (receipt) => ["OPEN", "POSTING"].includes(receipt.status)
  ).length ?? 0;
  const openCount = openAsnCount + openAdHocCount;
  const closedCount = (asns.data?.length ?? 0) - openAsnCount
    + (adHocReceipts.data?.length ?? 0) - openAdHocCount;

  const addAsn = useMutation({
    mutationFn: () => createAsn({
      organizationId,
      clientId: stockOwnerId,
      warehouseId,
      supplierId,
      asnNumber: asnNumbering?.automatic ? null : asnNumber,
      externalReference: externalReference || null,
      purchaseOrderNumber: purchaseOrder || null,
      expectedArrivalAt: expectedArrival
        ? new Date(expectedArrival).toISOString()
        : null,
      customsClearanceRequired
      , replenishmentExecutionId: poSuggestionId || null,
      suggestedItemId: poSuggestionId && suggestedItemId ? suggestedItemId : null,
      suggestedQuantity: poSuggestionId && suggestedQuantity ? Number(suggestedQuantity) : null,
      suggestedUom: poSuggestionId ? suggestedUom : null
    }),
    onSuccess: async (asn) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["asns"] }),
        queryClient.invalidateQueries({ queryKey: ["numbering-policies"] })
      ]);
      setAsnOpen(false);
      setExpandedAsnIds((current) => [...current, asn.id]);
      resetAsnDraft();
    }
  });
  const lookupPurchaseOrder = useMutation({
    mutationFn: () => loadPurchaseOrderSuggestion(purchaseOrder.trim()),
    onSuccess: (suggestion) => {
      setPoSuggestionId(suggestion.replenishmentExecutionId);
      if (suggestion.clientId) setStockOwnerId(suggestion.clientId);
      setWarehouseId(suggestion.warehouseId);
      if (suggestion.supplierId) setSupplierId(suggestion.supplierId);
      setSuggestedItemId(suggestion.itemId);
      setSuggestedQuantity(String(suggestion.remainingQuantity));
      setSuggestedUom(suggestion.uom);
      if (suggestion.externalReference) setExternalReference(suggestion.externalReference);
      if (suggestion.expectedAt) setExpectedArrival(suggestion.expectedAt.slice(0, 16));
    }
  });
  const resetAsnDraft = () => {
    setAsnNumber("");
    setSupplierId("");
    setStockOwnerId("");
    setWarehouseId("");
    setPurchaseOrder("");
    setPoSuggestionId("");
    setSuggestedItemId("");
    setSuggestedQuantity("");
    setSuggestedUom("EA");
    setExternalReference("");
    setExpectedArrival("");
    setCustomsClearanceRequired(false);
    lookupPurchaseOrder.reset();
  };
  const quickStatusChange = useMutation({
    mutationFn: ({ asn, status }: { asn: Asn; status: Asn["status"] }) =>
      changeAsnStatus(asn.id, status, asn.version),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["asns"] });
    }
  });
  const customsAction = useMutation({
    mutationFn: (asn: Asn) => claimAsnCustomsClearance(asn),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["asns"] });
    }
  });
  const addAdHocReceipt = useMutation({
    mutationFn: () => createAdHocReceipt({
      organizationId,
      clientId: adHocStockOwnerId,
      warehouseId: adHocWarehouseId,
      supplierId: adHocSupplierId,
      receiptNumber: receiptNumbering?.automatic
        ? null : adHocReceiptNumber,
      reasonCode: adHocReasonCode,
      reasonComment: adHocReasonComment
    }),
    onSuccess: async (receipt) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["ad-hoc-receipts"] }),
        queryClient.invalidateQueries({ queryKey: ["numbering-policies"] })
      ]);
      setAdHocOpen(false);
      setActiveAdHocReceipt(receipt);
      setAdHocReceiptNumber("");
      setAdHocStockOwnerId("");
      setAdHocReasonComment("");
    }
  });

  function submitAsn(event: FormEvent) {
    event.preventDefault();
    addAsn.mutate();
  }

  function submitAdHoc(event: FormEvent) {
    event.preventDefault();
    addAdHocReceipt.mutate();
  }

  useEffect(() => setMobileSelection(null), [listTab]);

  const selectedMobileAsn = mobileSelection?.kind === "asn"
    ? filteredAsns.find((asn) => asn.id === mobileSelection.id) ?? null : null;
  const selectedMobileReceipt = mobileSelection?.kind === "receipt"
    ? filteredAdHocReceipts.find((receipt) =>
      receipt.id === mobileSelection.id) ?? null : null;
  const mobilePrimaryLabel = selectedMobileReceipt
    ? (selectedMobileReceipt.status === "COMPLETED" ? t("inbound.actions.view") : t("common.actions.continue"))
    : selectedMobileAsn?.status === "DRAFT" ? t("inbound.actions.markExpected")
      : selectedMobileAsn?.status === "EXPECTED" ? t("inbound.actions.markArrival")
        : selectedMobileAsn?.status === "CUSTOMS_PENDING"
          ? selectedMobileAsn.customsClearanceStatus === "OPEN"
            ? t("inbound.actions.takeCharge") : t("inbound.actions.enterDecision")
          : selectedMobileAsn?.status === "ARRIVED" ? t("common.actions.start")
            : selectedMobileAsn?.status === "RECEIVING" ? t("common.actions.continue")
              : selectedMobileAsn?.status === "RECEIVED" ? t("inbound.actions.closeAsn")
                : selectedMobileAsn ? t("inbound.actions.view") : t("inbound.actions.select");
  const mobilePrimaryAvailable = Boolean(selectedMobileReceipt
    || selectedMobileAsn) && !(selectedMobileAsn?.status === "DRAFT"
      && selectedMobileAsn.lineCount === 0)
    && !(selectedMobileAsn?.status === "CUSTOMS_PENDING"
      && selectedMobileAsn.customsClearanceStatus === "REFUSED");
  useEffect(() => {
    const asn = selected ?? selectedMobileAsn;
    const receipt = activeAdHocReceipt ?? selectedMobileReceipt;
    setDiagnosticContext({
      screen: t("inbound.module"),
      action: selected ? `ASN ${selected.asnNumber}`
        : activeAdHocReceipt ? `${t("inbound.module")} ${activeAdHocReceipt.receiptNumber}` : listTab,
      entities: [
        ...(asn ? [{ type: "ASN" as const, identifier: asn.asnNumber,
          status: asn.status }] : []),
        ...(receipt ? [{ type: "REC" as const, identifier: receipt.receiptNumber,
          status: receipt.status }] : []),
        ...((asn?.handlingUnitNumbers ?? "").split(",").map((value) => value.trim())
          .filter(Boolean).map((identifier) => ({ type: "HU" as const, identifier })))
      ]
    });
  }, [selected, selectedMobileAsn, activeAdHocReceipt,
    selectedMobileReceipt, listTab]);
  const executeMobilePrimary = () => {
    if (selectedMobileReceipt) {
      setActiveAdHocReceipt(selectedMobileReceipt);
      return;
    }
    if (!selectedMobileAsn) return;
    if (selectedMobileAsn.status === "DRAFT") {
      quickStatusChange.mutate({ asn: selectedMobileAsn, status: "EXPECTED" });
    } else if (selectedMobileAsn.status === "EXPECTED") {
      quickStatusChange.mutate({ asn: selectedMobileAsn, status: "ARRIVED" });
    } else if (selectedMobileAsn.status === "CUSTOMS_PENDING"
      && selectedMobileAsn.customsClearanceStatus === "OPEN") {
      customsAction.mutate(selectedMobileAsn);
    } else if (selectedMobileAsn.status === "RECEIVED") {
      quickStatusChange.mutate({ asn: selectedMobileAsn, status: "CLOSED" });
    } else {
      setSelected(selectedMobileAsn);
    }
  };
  const statusTabs = [
    { value: "open" as const, label: t("inbound.tabs.open"),
      mobileLabel: t("inbound.tabsMobile.open"), count: openCount },
    { value: "closed" as const, label: t("inbound.tabs.closed"),
      mobileLabel: t("inbound.tabsMobile.closed"), count: closedCount }
  ];

  return (
    <Stack spacing={1.5} sx={{
      height: { xs: "100%", sm: "auto", md: "100%" },
      minHeight: 0,
      overflow: { xs: "hidden", sm: "visible", md: "hidden" }
    }}>
      <MobileOperationalHeader moduleLabel={t("inbound.module")}
        search={syntaxSearch} onSearchChange={setSyntaxSearch}
        searchPlaceholder={t("inbound.searchPlaceholder")} onBack={onBack} />
      <Box sx={{ flexShrink: 0, display: { xs: "none", sm: "block" } }}>
        <PageHeader title={t("inbound.title")} compact onBack={onBack}
          actions={<Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<AddOutlined />}
              onClick={() => setAdHocOpen(true)}>{t("inbound.actions.adHoc")}</Button>
            <Button variant="contained" startIcon={<MoveToInboxOutlined />}
              onClick={() => setAsnOpen(true)}>{t("inbound.actions.newAsn")}</Button>
          </Stack>} />
      </Box>

      <ResponsiveStatusTabs value={listTab} items={statusTabs}
        onChange={setListTab} />

      <Card variant="outlined" sx={{ display: { xs: "none", sm: "block" },
        flexShrink: 0 }}>
        <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
          <Stack spacing={1.5}>
            <TextField
              size="small"
              label={t("common.filters.search")}
              placeholder={t("inbound.searchPlaceholder")}
              fullWidth
              value={syntaxSearch}
              onChange={(event) => setSyntaxSearch(event.target.value)}
              InputProps={{
                startAdornment: <SearchOutlined color="action" sx={{ mr: 1 }} />
              }}
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
              <FormControl size="small" sx={{ minWidth: 190 }}>
                <InputLabel>{t("inbound.filters.sortDate")}</InputLabel>
                <Select label={t("inbound.filters.sortDate")} value={sortDate}
                  onChange={(event) => setSortDate(event.target.value as
                    "createdAt" | "expectedArrivalAt")}>
                  <MenuItem value="createdAt">{t("inbound.filters.createdAt")}</MenuItem>
                  <MenuItem value="expectedArrivalAt">{t("inbound.filters.expectedArrival")}</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel>{t("inbound.filters.order")}</InputLabel>
                <Select label={t("inbound.filters.order")} value={sortDirection}
                  onChange={(event) => setSortDirection(event.target.value as
                    "asc" | "desc")}>
                  <MenuItem value="asc">{t("common.filters.ascending")}</MenuItem>
                  <MenuItem value="desc">{t("common.filters.descending")}</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {asns.isError && (
        <Alert
          severity="error"
          action={isAuthenticationError(asns.error)
            ? <Button color="inherit" size="small" onClick={() => void login()}>
                {t("common.actions.reconnect")}
              </Button>
            : <Button color="inherit" size="small" onClick={() => void asns.refetch()}>
                {t("common.actions.retry")}
              </Button>}
        >
          {t("inbound.errors.load")}
        </Alert>
      )}
      {!asns.isLoading && !asns.isError && !adHocReceipts.isLoading
        && !adHocReceipts.isError && asns.data?.length === 0
        && adHocReceipts.data?.length === 0 && (
        <Alert severity="info">
          {t("inbound.empty.all")}
        </Alert>
      )}

      <Box sx={{
        minHeight: 0,
        flex: { xs: "1 1 0", sm: "none", md: 1 },
        overflowY: { xs: "auto", sm: "visible", md: "auto" },
        pr: .5,
        pb: { xs: 11, md: 0 }
      }}>
      {quickStatusChange.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("inbound.errors.statusChange")}
        </Alert>
      )}
      {!asns.isLoading && !asns.isError && !adHocReceipts.isLoading
        && !adHocReceipts.isError
        && (asns.data?.length !== 0 || adHocReceipts.data?.length !== 0)
        && filteredAsns.length === 0 && filteredAdHocReceipts.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {t("inbound.empty.filtered")}
          </Alert>
        )}
      <Stack spacing={1.5}>
        {filteredAdHocReceipts.map((receipt) => (
          <Card key={receipt.id} variant="outlined"
            onClick={(event) => {
              if ((event.target as HTMLElement).closest(
                "button,a,[role='button']")) return;
              setMobileSelection({ kind: "receipt", id: receipt.id });
            }}
            sx={{ cursor: { xs: "pointer", sm: "default" },
              outline: { xs: mobileSelection?.kind === "receipt"
                && mobileSelection.id === receipt.id
                ? "2px solid" : "none", sm: "none" },
              outlineColor: "primary.main" }}>
            <CardContent>
              <Stack direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1, sm: 2 }}
                alignItems={{ sm: "center" }}>
                <MoveToInboxOutlined color="secondary" />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <LabeledInfo label={t("inbound.fields.receiptNumber")} prominent>
                      {receipt.receiptNumber}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.fields.supplier")} prominent>
                      {receipt.supplierName}
                    </LabeledInfo>
                    </Stack>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <LabeledInfo label={t("inbound.fields.warehouse")}>
                      {warehouseCodes.get(receipt.warehouseId) ?? "—"}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.card.labels.lines")}>
                      {receipt.lineCount}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.card.labels.type")}>
                      {t("inbound.card.labels.adHoc")}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.fields.reason")}>
                      {t(`inbound.adHoc.reasons.${receipt.reasonCode}`, { defaultValue: receipt.reasonCode })}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.filters.createdAt")}>
                      {new Date(receipt.startedAt).toLocaleString(i18n.resolvedLanguage)}
                    </LabeledInfo>
                    </Stack>
                  </Stack>
                  <SourceTrace sources={[receipt.receiptNumber]} />
                </Box>
                <ListRowActions status={
                  <Chip label={receiptStatusLabel(receipt.status)}
                    color={operationalStatusColor(receipt.status)} />
                } />
                <IconButton className="mobile-card-expand"
                  aria-label={t("inbound.card.detailsAria")}
                  onClick={() => setExpandedAdHocReceiptIds((current) =>
                    current.includes(receipt.id)
                      ? current.filter((id) => id !== receipt.id)
                      : [...current, receipt.id])}
                  sx={{ transform: expandedAdHocReceiptIds.includes(receipt.id)
                    ? "rotate(180deg)" : "none" }}>
                  <ExpandMoreOutlined />
                </IconButton>
              </Stack>
              <Collapse in={expandedAdHocReceiptIds.includes(receipt.id)}>
                <AdHocReceiptInlineSummary receipt={receipt}
                  onContinue={() => setActiveAdHocReceipt(receipt)} />
              </Collapse>
            </CardContent>
          </Card>
        ))}
        {filteredAsns.map((asn) => (
          <Card key={asn.id} id={`asn-${asn.id}`} variant="outlined"
            onClick={(event) => {
              if ((event.target as HTMLElement).closest(
                "button,a,[role='button']")) return;
              setMobileSelection({ kind: "asn", id: asn.id });
            }}
            sx={{
              cursor: { xs: "pointer", sm: "default" },
              outline: { xs: mobileSelection?.kind === "asn"
                && mobileSelection.id === asn.id
                ? "2px solid" : "none", sm: "none" },
              outlineColor: "primary.main",
              borderColor: asn.id === initialTargetId ? "primary.main" : undefined,
              borderWidth: asn.id === initialTargetId ? 2 : undefined
            }}>
            <CardContent>
              <Stack direction={{ xs: "column", sm: "row" }}
                spacing={{ xs: 1, sm: 2 }}
                alignItems={{ sm: "center" }}>
                <MoveToInboxOutlined color="secondary" />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <LabeledInfo label={t("inbound.fields.asnNumber")} prominent>
                      {asn.asnNumber}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.fields.supplier")} prominent>
                      {asn.supplierName}
                    </LabeledInfo>
                    </Stack>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <LabeledInfo label={t("inbound.fields.warehouse")}>
                      {asn.warehouseCode}
                    </LabeledInfo>
                    {asn.clientName && (<LabeledInfo label={t("inbound.card.labels.client")}>
                      {asn.clientName}{asn.clientCode ? ` (${asn.clientCode})` : ""}
                    </LabeledInfo>)}
                    {asn.purchaseOrderNumber && (<LabeledInfo label={t("inbound.fields.purchaseOrder")}>
                      {asn.purchaseOrderNumber}
                    </LabeledInfo>)}
                    <LabeledInfo label={t("inbound.card.labels.lines")}>
                      {asn.lineCount}
                    </LabeledInfo>
                    </Stack>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    {asn.expectedArrivalAt && (<LabeledInfo label={t("inbound.fields.expectedArrival")}>
                      {new Date(asn.expectedArrivalAt).toLocaleString(i18n.resolvedLanguage)}
                    </LabeledInfo>)}
                    <LabeledInfo label={t("inbound.filters.createdAt")}>
                      {new Date(asn.createdAt).toLocaleString(i18n.resolvedLanguage)}
                    </LabeledInfo>
                    </Stack>
                  </Stack>
                  <SourceTrace sources={[
                    asn.asnNumber,
                    ...(asn.customsClearanceNumber ? [asn.customsClearanceNumber] : []),
                    ...(asn.receiptNumbers?.split(", ") ?? []),
                    ...(asn.handlingUnitNumbers?.split(", ") ?? [])
                  ]} compactPrefix="HU-" />
                </Box>
                <ListRowActions
                  actions={
                    asn.status === "DRAFT" ? (
                      <Button variant="contained"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        disabled={quickStatusChange.isPending || asn.lineCount === 0}
                        onClick={() => quickStatusChange.mutate({ asn, status: "EXPECTED" })}>
                        {t("inbound.actions.markExpected")}
                      </Button>
                    ) : asn.status === "EXPECTED" ? (
                      <Button variant="contained"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        disabled={quickStatusChange.isPending}
                        onClick={() => quickStatusChange.mutate({ asn, status: "ARRIVED" })}>
                        {t("inbound.actions.markArrival")}
                      </Button>
                    ) : asn.status === "CUSTOMS_PENDING" ? (
                      <Button variant="contained"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        disabled={customsAction.isPending
                          || asn.customsClearanceStatus === "REFUSED"}
                        onClick={() => asn.customsClearanceStatus === "OPEN"
                          ? customsAction.mutate(asn) : setSelected(asn)}>
                        {asn.customsClearanceStatus === "OPEN"
                          ? t("inbound.actions.takeCharge")
                          : asn.customsClearanceStatus === "REFUSED"
                            ? t("inbound.actions.customsRejected")
                            : t("inbound.actions.enterDecision")}
                      </Button>
                    ) : ["ARRIVED", "RECEIVING"].includes(asn.status) ? (
                      <Button variant="contained" startIcon={<MoveToInboxOutlined />}
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        onClick={() => setSelected(asn)}>
                        {asn.status === "ARRIVED"
                          ? t("common.actions.start") : t("common.actions.continue")}
                      </Button>
                    ) : asn.status === "RECEIVED" ? (
                      <Button variant="contained"
                        sx={{ display: { xs: "none", sm: "inline-flex" } }}
                        disabled={quickStatusChange.isPending}
                        onClick={() => quickStatusChange.mutate({ asn, status: "CLOSED" })}>
                        {t("inbound.actions.closeAsn")}
                      </Button>
                    ) : undefined
                  }
                  status={
                    <Chip color={statusColors[asn.status]}
                      variant={asn.status === "RECEIVED" ? "outlined" : "filled"}
                      label={operationalStatusLabel(asn.status)} />
                  }
                />
                <IconButton className="mobile-card-expand"
                  aria-label={t("inbound.card.detailsAria")}
                  onClick={() => setExpandedAsnIds((current) =>
                    current.includes(asn.id)
                      ? current.filter((id) => id !== asn.id)
                      : [...current, asn.id]
                  )} sx={{ width: 40, height: 40,
                    alignSelf: { xs: "center", sm: "auto" } }}>
                  <ExpandMoreOutlined sx={{ transition: "transform .2s",
                    transform: expandedAsnIds.includes(asn.id)
                      ? "rotate(180deg)" : "rotate(0deg)" }} />
                </IconButton>
              </Stack>
              <Collapse in={expandedAsnIds.includes(asn.id)} unmountOnExit>
                <AsnInlineSummary asn={asn}
                  onOpenOperations={() => setSelected(asn)} />
              </Collapse>
            </CardContent>
          </Card>
        ))}
      </Stack>
      </Box>

      <MobileActionBar
        secondaryIcon={<AddOutlined />}
        secondaryLabel={t("inbound.actions.adHoc")} secondaryDisplayLabel={t("inbound.actions.adHoc")}
        onSecondary={() => setAdHocOpen(true)}
        additionalIcon={<MoveToInboxOutlined />}
        additionalLabel={t("inbound.actions.newAsn")} additionalDisplayLabel="ASN"
        onAdditional={() => setAsnOpen(true)}
        primaryLabel={mobilePrimaryLabel}
        primaryDisabled={!mobilePrimaryAvailable}
        onPrimary={executeMobilePrimary} />

      <Dialog open={asnOpen} onClose={() => { setAsnOpen(false); resetAsnDraft(); }}
        fullWidth maxWidth="sm">
        <Box component="form" onSubmit={submitAsn}>
          <DialogTitle>{t("inbound.actions.createAsn")}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField label={t("inbound.fields.asnNumber")}
                required={!asnNumbering?.automatic}
                disabled={asnNumbering?.automatic}
                value={asnNumbering?.automatic
                  ? asnNumbering.preview ?? t("inbound.numbering.automatic")
                  : asnNumber}
                helperText={asnNumbering?.automatic
                  ? t("inbound.numbering.preview") : t("inbound.numbering.manual")}
                onChange={(event) => setAsnNumber(event.target.value.toUpperCase())} />
              <FormControl fullWidth required>
                <InputLabel>{t("inbound.fields.supplier")}</InputLabel>
                <Select value={supplierId} label={t("inbound.fields.supplier")}
                  data-testid="asn-supplier"
                  onChange={(event) => setSupplierId(event.target.value)}>
                  {suppliers.data?.filter((supplier) => supplier.status === "ACTIVE")
                    .map((supplier) => (
                      <MenuItem key={supplier.id} value={supplier.id}>
                        {supplier.code} · {supplier.displayName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>{t("inbound.fields.stockOwner")}</InputLabel>
                <Select value={stockOwnerId} label={t("inbound.fields.stockOwner")}
                  data-testid="asn-stock-owner"
                  onChange={(event) => setStockOwnerId(event.target.value)}>
                  {stockOwners.data?.filter((owner) => owner.status === "ACTIVE")
                    .map((owner) => (
                      <MenuItem key={owner.id} value={owner.id}>
                        {owner.code} · {owner.displayName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>{t("inbound.fields.warehouse")}</InputLabel>
                <Select value={warehouseId} label={t("inbound.fields.warehouse")}
                  data-testid="asn-warehouse"
                  onChange={(event) => setWarehouseId(event.target.value)}>
                  {warehouses.map((warehouse) => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.code} · {warehouse.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField fullWidth label={t("inbound.fields.purchaseOrder")} value={purchaseOrder}
                  onChange={(event) => { setPurchaseOrder(event.target.value); setPoSuggestionId(""); }} />
                <Button variant="outlined" disabled={!purchaseOrder.trim() || lookupPurchaseOrder.isPending}
                  onClick={() => lookupPurchaseOrder.mutate()} sx={{ minWidth: 120 }}>
                  {lookupPurchaseOrder.isPending ? <CircularProgress size={20} /> : t("inbound.actions.findPo")}
                </Button>
              </Stack>
              {lookupPurchaseOrder.isError && <Alert severity="warning">
                {t("inbound.errors.poNotFound")}</Alert>}
              {poSuggestionId && <Alert severity={supplierId ? "success" : "warning"}>
                {t("inbound.labels.poPrefilled")}{!supplierId && ` ${t("inbound.labels.poSupplierMissing")}`}
              </Alert>}
              {poSuggestionId && <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <FormControl fullWidth required><InputLabel>{t("inbound.fields.item")}</InputLabel>
                  <Select value={suggestedItemId} label={t("inbound.fields.item")}
                    onChange={(event) => { const id = event.target.value; setSuggestedItemId(id);
                      setSuggestedUom(asnProducts.data?.find((item) => item.id === id)?.baseUom ?? "EA"); }}>
                    {asnProducts.data?.filter((item) => item.status === "ACTIVE").map((item) =>
                      <MenuItem key={item.id} value={item.id}>{item.code} · {item.displayName}</MenuItem>)}
                  </Select></FormControl>
                <TextField required type="number" label={t("inbound.fields.quantity")}
                  value={suggestedQuantity} onChange={(event) => setSuggestedQuantity(event.target.value)}
                  slotProps={{ htmlInput: { min: 0.000001, step: "any" } }} sx={{ width: { sm: 180 } }} />
                <TextField label={t("inbound.fields.uom")} value={suggestedUom}
                  onChange={(event) => setSuggestedUom(event.target.value)} sx={{ width: { sm: 120 } }} />
              </Stack>}
              <TextField label={t("inbound.fields.externalReference")} value={externalReference}
                onChange={(event) => setExternalReference(event.target.value)} />
              <TextField label={t("inbound.fields.expectedArrival")} type="datetime-local"
                required
                value={expectedArrival} InputLabelProps={{ shrink: true }}
                onChange={(event) => setExpectedArrival(event.target.value)} />
              <FormControlLabel
                control={<Switch checked={customsClearanceRequired}
                  onChange={(event) => setCustomsClearanceRequired(
                    event.target.checked
                  )} />}
                label={t("inbound.fields.customsRequired")}
              />
              {addAsn.isError && (
                <Alert severity="error">
                  {t("inbound.errors.asnCreate")}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => { setAsnOpen(false); resetAsnDraft(); }}>
              {t("common.actions.cancel")}</Button>
            <Button type="submit" variant="contained"
              disabled={addAsn.isPending || !supplierId || !warehouseId ||
                !stockOwnerId ||
                !expectedArrival ||
                (!asnNumbering?.automatic && !asnNumber.trim())}>
              {t("inbound.actions.create")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={adHocOpen} onClose={() => setAdHocOpen(false)}
        fullWidth maxWidth="sm">
        <Box component="form" onSubmit={submitAdHoc}>
          <DialogTitle>{t("inbound.adHoc.title")}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="warning">
                {t("inbound.adHoc.warning")}
              </Alert>
              <TextField label={t("inbound.fields.receiptNumber")}
                required={Boolean(receiptNumbering && !receiptNumbering.automatic)}
                disabled={numbering.isPending || numberingUnavailable
                  || receiptNumbering?.automatic}
                value={receiptNumbering?.automatic
                  ? receiptNumbering.preview ?? t("inbound.numbering.automatic")
                  : adHocReceiptNumber}
                helperText={numbering.isPending
                  ? t("inbound.numbering.loading")
                  : numberingUnavailable
                    ? t("inbound.numbering.unavailable")
                    : receiptNumbering?.automatic
                  ? t("inbound.numbering.preview") : t("inbound.numbering.manual")}
                onChange={(event) =>
                  setAdHocReceiptNumber(event.target.value.toUpperCase())} />
              <FormControl fullWidth required>
                <InputLabel>{t("inbound.fields.supplier")}</InputLabel>
                <Select value={adHocSupplierId} label={t("inbound.fields.supplier")}
                  onChange={(event) => setAdHocSupplierId(event.target.value)}>
                  {suppliers.data?.filter((supplier) =>
                    supplier.status === "ACTIVE"
                  ).map((supplier) => (
                    <MenuItem key={supplier.id} value={supplier.id}>
                      {supplier.code} · {supplier.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>{t("inbound.fields.stockOwner")}</InputLabel>
                <Select value={adHocStockOwnerId}
                  label={t("inbound.fields.stockOwner")}
                  onChange={(event) =>
                    setAdHocStockOwnerId(event.target.value)}>
                  {stockOwners.data?.filter((owner) => owner.status === "ACTIVE")
                    .map((owner) => (
                      <MenuItem key={owner.id} value={owner.id}>
                        {owner.code} · {owner.displayName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>{t("inbound.fields.warehouse")}</InputLabel>
                <Select value={adHocWarehouseId} label={t("inbound.fields.warehouse")}
                  onChange={(event) => setAdHocWarehouseId(event.target.value)}>
                  {warehouses.map((warehouse) => (
                    <MenuItem key={warehouse.id} value={warehouse.id}>
                      {warehouse.code} · {warehouse.displayName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth required>
                <InputLabel>{t("inbound.fields.reason")}</InputLabel>
                <Select value={adHocReasonCode} label={t("inbound.fields.reason")}
                  onChange={(event) => setAdHocReasonCode(event.target.value)}>
                  <MenuItem value="UNPLANNED_DELIVERY">
                    {t("inbound.adHoc.reasons.UNPLANNED_DELIVERY")}
                  </MenuItem>
                  <MenuItem value="ERP_UNAVAILABLE">{t("inbound.adHoc.reasons.ERP_UNAVAILABLE")}</MenuItem>
                  <MenuItem value="EMERGENCY">{t("inbound.adHoc.reasons.EMERGENCY")}</MenuItem>
                  <MenuItem value="OTHER">{t("inbound.adHoc.reasons.OTHER")}</MenuItem>
                </Select>
              </FormControl>
              <TextField required multiline minRows={3}
                label={t("inbound.fields.comment")}
                value={adHocReasonComment}
                onChange={(event) => setAdHocReasonComment(event.target.value)}
                inputProps={{ maxLength: 500 }} />
              {addAdHocReceipt.isError && (
                <Alert severity="error">
                  {t("inbound.errors.adHocCreate")}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAdHocOpen(false)}>{t("common.actions.cancel")}</Button>
            <Button type="submit" variant="contained"
              disabled={addAdHocReceipt.isPending || !adHocSupplierId
                || !adHocStockOwnerId || !adHocWarehouseId || !adHocReasonCode
                || !adHocReasonComment.trim()
                || (!receiptNumbering?.automatic
                  && !adHocReceiptNumber.trim())}>
              {t("inbound.actions.createAndReceive")}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <AsnDetail asn={selected} onAsnChange={setSelected}
        onClose={() => setSelected(null)} />
      <ReceiptExecution receipt={activeAdHocReceipt} asn={null} asnLines={[]}
        onClose={() => setActiveAdHocReceipt(null)}
        onChanged={async () => {
          await queryClient.invalidateQueries({
            queryKey: ["ad-hoc-receipts"]
          });
        }}
        onCompleted={async (receipt) => {
          setActiveAdHocReceipt(receipt);
          await queryClient.invalidateQueries({
            queryKey: ["ad-hoc-receipts"]
          });
        }} />
    </Stack>
  );
}

function AdHocReceiptInlineSummary({ receipt, onContinue }: {
  receipt: Receipt;
  onContinue: () => void;
}) {
  const { t } = useTranslation();
  const lines = useQuery({
    queryKey: ["receipt-lines", receipt.id],
    queryFn: () => loadReceiptLines(receipt.id)
  });
  return (
    <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: "divider" }}>
      <Stack spacing={1}>
        {lines.data?.map((line) => (
          <Stack key={line.id} direction={{ xs: "column", md: "row" }}
            justifyContent="space-between" spacing={1}
            sx={{ p: 1.25, bgcolor: "action.hover", borderRadius: 1 }}>
            <Typography variant="body2" fontWeight={750}>
              {line.lineNumber} · {line.itemCode} · {line.itemName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("inbound.quantities.received", { received: line.receivedQuantity,
                uom: line.actualUom, accepted: line.acceptedQuantity })}
              {line.quarantinedQuantity ? t("inbound.quantities.quarantined",
                { count: line.quarantinedQuantity }) : ""}
              {line.rejectedQuantity ? t("inbound.quantities.rejected",
                { count: line.rejectedQuantity }) : ""}
            </Typography>
          </Stack>
        ))}
        {lines.isLoading && <CircularProgress size={22} />}
        {lines.isError && <Alert severity="warning">{t("inbound.errors.lines")}</Alert>}
      </Stack>
      {["OPEN", "POSTING"].includes(receipt.status) && (
        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1.5 }}>
          <Button variant="contained" onClick={onContinue}>{t("inbound.actions.continueReceipt")}</Button>
        </Stack>
      )}
    </Box>
  );
}

function AsnInlineSummary({ asn, onOpenOperations }: {
  asn: Asn;
  onOpenOperations: () => void;
}) {
  const { t, i18n: translation } = useTranslation();
  const lines = useQuery({
    queryKey: ["asn-lines", asn.id], queryFn: () => loadAsnLines(asn.id)
  });
  const receipts = useQuery({
    queryKey: ["receipts", asn.id], queryFn: () => loadReceipts(asn.id)
  });
  const documents = useQuery({
    queryKey: ["generated-asn-documents", asn.id],
    queryFn: () => loadGeneratedAsnDocuments(asn.id)
  });
  const audit = useQuery({
    queryKey: ["asn-audit", asn.id], queryFn: () => loadAsnAudit(asn.id)
  });
  const openDocument = useMutation({
    mutationFn: async ({ id, filename }: { id: string; filename: string }) => {
      const preview = window.open("", "_blank");
      try {
        const blob = await loadGeneratedAsnDocumentContent(asn.id, id);
        const url = URL.createObjectURL(blob);
        if (preview) {
          preview.document.title = filename;
          preview.location.href = url;
        } else {
          window.open(url, "_blank");
        }
        window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } catch (error) {
        preview?.close();
        throw error;
      }
    }
  });
  const hasError = lines.isError || receipts.isError || documents.isError || audit.isError;

  return (
    <Box sx={{ mt: { xs: 1, sm: 2 }, ml: { sm: 5 },
      p: { xs: 1.5, sm: 2 }, bgcolor: "action.hover", borderRadius: 1.5 }}>
      <Stack spacing={2}>
        {hasError && <Alert severity="warning">{t("inbound.errors.partialDetails")}</Alert>}
        <Box>
          <Typography fontWeight={800} sx={{ mb: 1 }}>{t("inbound.sections.expectedLines")}</Typography>
          <Stack spacing={1}>
            {lines.data?.map((line) => (
              <Box key={line.id} sx={{ p: 1.25, bgcolor: "background.paper", border: 1,
                borderColor: "divider", borderRadius: 1 }}>
                <Stack spacing={1}>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <LabeledInfo label={t("inbound.card.labels.line")}>
                      {line.lineNumber}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.fields.item")} prominent>
                      {line.itemCode}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.card.labels.description")} prominent>
                      {line.itemName}
                    </LabeledInfo>
                    </Stack>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <LabeledInfo label={t("inbound.card.labels.expected")}>
                      {line.expectedQuantity} {line.expectedUom}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.fields.received")}>
                      {line.receivedQuantity} {line.expectedUom}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.card.labels.remaining")}>
                      {line.remainingQuantity} {line.expectedUom}
                    </LabeledInfo>
                    {line.packagingCode && (<LabeledInfo label={t("inbound.fields.packaging")}>
                      {line.packagingCode}
                    </LabeledInfo>)}
                    {line.expectedLotNumber && (<LabeledInfo label={t("inbound.fields.expectedLot")}>
                      {line.expectedLotNumber}
                    </LabeledInfo>)}
                    </Stack>
                  </Stack>
              </Box>
            ))}
            {!lines.isLoading && lines.data?.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                {t("inbound.emptyDetails.lines")}
              </Typography>
            )}
          </Stack>
        </Box>

        {(receipts.data?.length ?? 0) > 0 && (
          <Box>
            <Typography fontWeight={800} sx={{ mb: 1 }}>{t("inbound.sections.receipts")}</Typography>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              {receipts.data?.map((receipt) => (
                <Chip key={receipt.id}
                  label={`${receipt.receiptNumber} · ${receiptStatusLabel(receipt.status)}`}
                  color={operationalStatusColor(receipt.status)}
                  variant="outlined" />
              ))}
            </Stack>
          </Box>
        )}

        <Accordion disableGutters elevation={0} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
            <Typography fontWeight={800}>{t("inbound.sections.documents", { count: documents.data?.length ?? 0 })}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {openDocument.isError && <Alert severity="error">{t("inbound.errors.documentOpen")}</Alert>}
              {documents.data?.map((document) => (
                <Stack key={document.id} direction={{ xs: "column", sm: "row" }} spacing={1}
                  alignItems={{ sm: "center" }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight={700}>
                      {document.number} · {document.filename}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(document.generatedAt).toLocaleString(translation.resolvedLanguage)}
                    </Typography>
                  </Box>
                  <Button size="small" variant="outlined" startIcon={<VisibilityOutlined />}
                    onClick={() => openDocument.mutate({ id: document.id,
                      filename: document.filename })}>
                    {t("inbound.actions.view")}
                  </Button>
                </Stack>
              ))}
              {!documents.isLoading && documents.data?.length === 0 && (
                <Typography variant="body2" color="text.secondary">{t("inbound.emptyDetails.documents")}</Typography>
              )}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Accordion disableGutters elevation={0} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
            <Typography fontWeight={800}>{t("inbound.sections.audit", { count: audit.data?.length ?? 0 })}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Stack spacing={1}>
              {audit.data?.map((entry) => (
                <Box key={entry.id}>
                  <Typography variant="body2" fontWeight={700}>
                    {entry.entityType} · {entry.action}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {new Date(entry.changedAt).toLocaleString(translation.resolvedLanguage)}{` · ${entry.changedBy}`}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>

        <Box>
          <Button variant="outlined" onClick={onOpenOperations}>
            {asn.status === "DRAFT" ? t("inbound.actions.editLines") : t("inbound.actions.operations")}
          </Button>
        </Box>
      </Stack>
    </Box>
  );
}

function AsnDetail({
  asn,
  onAsnChange,
  onClose
}: {
  asn: Asn | null;
  onAsnChange: (asn: Asn) => void;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const { t, i18n: translation } = useTranslation();
  const [itemId, setItemId] = useState("");
  const [packagingId, setPackagingId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [uom, setUom] = useState("EA");
  const [lotNumber, setLotNumber] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [receiptNumber, setReceiptNumber] = useState("");
  const [customsInvoicePresent, setCustomsInvoicePresent] = useState(false);
  const [customsPackingListPresent, setCustomsPackingListPresent] = useState(false);
  const [customsOriginProvided, setCustomsOriginProvided] = useState(false);
  const [customsReference, setCustomsReference] = useState("");
  const [customsBroker, setCustomsBroker] = useState("");
  const [customsPhysicalCheck, setCustomsPhysicalCheck] = useState(false);
  const [customsDecision, setCustomsDecision] = useState<
    "CLEARANCE_GRANTED" | "ADDITIONAL_INFO_REQUIRED" | "REFUSED"
  >("CLEARANCE_GRANTED");
  const [customsComment, setCustomsComment] = useState("");
  const [activeReceipt, setActiveReceipt] = useState<Receipt | null>(null);
  const [documentPreview, setDocumentPreview] = useState<{
    url: string; filename: string;
  } | null>(null);

  const lines = useQuery({
    queryKey: ["asn-lines", asn?.id],
    queryFn: () => loadAsnLines(asn!.id),
    enabled: Boolean(asn)
  });
  const audit = useQuery({
    queryKey: ["asn-audit", asn?.id],
    queryFn: () => loadAsnAudit(asn!.id),
    enabled: Boolean(asn)
  });
  const generatedDocuments = useQuery({
    queryKey: ["generated-asn-documents", asn?.id],
    queryFn: () => loadGeneratedAsnDocuments(asn!.id),
    enabled: Boolean(asn)
  });
  const receipts = useQuery({
    queryKey: ["receipts", asn?.id],
    queryFn: () => loadReceipts(asn!.id),
    enabled: Boolean(asn)
  });
  const numbering = useQuery({
    queryKey: ["numbering-policies"], queryFn: loadNumberingPolicies,
    enabled: Boolean(asn)
  });
  const receiptNumbering = numbering.data?.find(
    (policy) => policy.documentType === "RECEIPT"
  );
  const numberingUnavailable = numbering.isError
    || (!numbering.isPending && !receiptNumbering);
  const products = useQuery({
    queryKey: ["products"], queryFn: loadProducts, enabled: Boolean(asn)
  });
  const packaging = useQuery({
    queryKey: ["packaging", itemId],
    queryFn: () => loadPackaging(itemId),
    enabled: Boolean(asn && itemId)
  });
  const selectedItem: ProductItem | undefined =
    products.data?.find((item) => item.id === itemId);

  useEffect(() => {
    if (!asn?.id) return;
    let active = true;
    loadAsn(asn.id).then((refreshed) => {
      if (active) onAsnChange(refreshed);
    }).catch(() => {
      // The regular queries and mutations keep their own visible error states.
    });
    return () => {
      active = false;
    };
  }, [asn?.id, onAsnChange]);

  useEffect(() => {
    setPackagingId("");
    setUom(selectedItem?.baseUom ?? "EA");
  }, [itemId, selectedItem?.baseUom]);

  const addLine = useMutation({
    mutationFn: () => addAsnLine(asn!.id, {
      itemId,
      itemPackagingId: packagingId || null,
      expectedQuantity: Number(quantity),
      expectedUom: uom,
      expectedLotNumber: lotNumber || null,
      expectedExpirationAt: expirationDate || null
    }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["asn-lines", asn?.id] }),
        queryClient.invalidateQueries({ queryKey: ["asn-audit", asn?.id] }),
        queryClient.invalidateQueries({ queryKey: ["asns"] })
      ]);
      setItemId("");
      setPackagingId("");
      setQuantity("1");
      setLotNumber("");
      setExpirationDate("");
      onAsnChange({ ...asn!, lineCount: asn!.lineCount + 1 });
    }
  });
  const statusChange = useMutation({
    mutationFn: (status: Asn["status"]) =>
      changeAsnStatus(asn!.id, status, asn!.version),
    onSuccess: async (updated) => {
      onAsnChange(updated);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["asns"] }),
        queryClient.invalidateQueries({ queryKey: ["asn-audit", asn?.id] })
      ]);
    }
  });
  const customsAction = useMutation({
    mutationFn: () => asn!.customsClearanceStatus === "OPEN"
      ? claimAsnCustomsClearance(asn!)
      : completeAsnCustomsClearance(asn!, {
          invoicePresent: customsInvoicePresent,
          packingListPresent: customsPackingListPresent,
          originProvided: customsOriginProvided,
          customsReference,
          brokerName: customsBroker.trim() || null,
          physicalCheckCompleted: customsPhysicalCheck,
          decision: customsDecision,
          comment: customsComment.trim() || null
        }),
    onSuccess: async (updated) => {
      onAsnChange(updated);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["asns"] }),
        queryClient.invalidateQueries({ queryKey: ["asn-audit", asn?.id] })
      ]);
    }
  });
  const startReceipt = useMutation({
    mutationFn: () => createReceipt(
      asn!.id, receiptNumbering?.automatic ? null : receiptNumber
    ),
    onSuccess: async (receipt) => {
      setActiveReceipt(receipt);
      setReceiptNumber("");
      const refreshed = await loadAsn(asn!.id);
      onAsnChange(refreshed);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["asns"] }),
        queryClient.invalidateQueries({ queryKey: ["receipts", asn?.id] }),
        queryClient.invalidateQueries({ queryKey: ["asn-audit", asn?.id] }),
        queryClient.invalidateQueries({ queryKey: ["numbering-policies"] })
      ]);
    },
    onError: async () => {
      const refreshed = await loadAsn(asn!.id).catch(() => null);
      if (refreshed) onAsnChange(refreshed);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["asn-lines", asn?.id] }),
        queryClient.invalidateQueries({ queryKey: ["receipts", asn?.id] }),
        queryClient.invalidateQueries({ queryKey: ["asns"] })
      ]);
    }
  });
  const generateAsnDocument = useMutation({
    mutationFn: async () => {
      const [templates, data] = await Promise.all([
        loadDocumentTemplates(), loadInboundAsnDocumentData(asn!.id)
      ]);
      const published = templates.filter((template) =>
        template.status === "PUBLISHED"
        && template.dataContext === "INBOUND_ASN"
        && template.dictionaryVersion === data.dictionaryVersion
      );
      const template = published.find((candidate) =>
        candidate.name.toLocaleLowerCase("fr") === "reception_asn"
      ) ?? published[0];
      if (!template) {
        throw new Error(t("inbound.errors.templateMissing"));
      }
      const generated = await generateDocumentPdf(template, data);
      await saveGeneratedAsnDocument(
        asn!.id, template.id, generated.filename, generated.blob
      );
      return generated;
    },
    onSuccess: ({ blob, filename }) => {
      void queryClient.invalidateQueries({
        queryKey: ["generated-asn-documents", asn?.id]
      });
      setDocumentPreview((current) => {
        if (current) URL.revokeObjectURL(current.url);
        return { url: URL.createObjectURL(blob), filename };
      });
    }
  });

  const closeDocumentPreview = () => {
    setDocumentPreview((current) => {
      if (current) URL.revokeObjectURL(current.url);
      return null;
    });
  };

  const openStoredDocument = useMutation({
    mutationFn: async ({ id, filename }: { id: string; filename: string }) => ({
      blob: await loadGeneratedAsnDocumentContent(asn!.id, id), filename
    }),
    onSuccess: ({ blob, filename }) => {
      setDocumentPreview((current) => {
        if (current) URL.revokeObjectURL(current.url);
        return { url: URL.createObjectURL(blob), filename };
      });
    }
  });

  useEffect(() => {
    const openReceipt = receipts.data?.find((receipt) =>
      ["OPEN", "POSTING"].includes(receipt.status));
    if (openReceipt) setActiveReceipt(openReceipt);
  }, [receipts.data]);

  const nextStatus =
    asn?.status === "DRAFT" ? "EXPECTED" :
    asn?.status === "EXPECTED" ? "ARRIVED" :
    asn?.status === "RECEIVED" ? "CLOSED" : null;

  return (
    <>
    <Dialog open={Boolean(asn)} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{asn?.asnNumber} · {asn?.supplierName}</DialogTitle>
      <DialogContent>
        {asn && (
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.75}
                justifyContent={{ xs: "flex-start", sm: "flex-end" }}>
                <Button size="small" variant="outlined"
                  startIcon={<PictureAsPdfOutlined />}
                  sx={{ whiteSpace: "nowrap", minHeight: 36, px: 1.5 }}
                  disabled={generateAsnDocument.isPending || !lines.data?.length}
                  onClick={() => generateAsnDocument.mutate()}>
                  {generateAsnDocument.isPending ? t("inbound.actions.generating") : t("inbound.actions.generateDocument")}
                </Button>
                {nextStatus && (
                  <Button size="small" variant="contained"
                    sx={{ whiteSpace: "nowrap", minHeight: 36, px: 1.5 }}
                    data-testid={`asn-transition-${nextStatus.toLowerCase()}`}
                    disabled={statusChange.isPending ||
                      (nextStatus === "EXPECTED" && asn.lineCount === 0)}
                    onClick={() => statusChange.mutate(nextStatus)}>
                    {operationalStatusLabel(nextStatus)}
                  </Button>
                )}
                {asn.status === "CUSTOMS_PENDING"
                  && asn.customsClearanceStatus === "OPEN" && (
                  <Button size="small" variant="contained"
                    disabled={customsAction.isPending}
                    onClick={() => customsAction.mutate()}>
                    {t("inbound.actions.takeCustoms")}
                  </Button>
                )}
                {["DRAFT", "EXPECTED", "CUSTOMS_PENDING", "ARRIVED"].includes(asn.status) && (
                  <Button size="small" color="error"
                    sx={{ whiteSpace: "nowrap", minHeight: 36, px: 1.5 }}
                    disabled={statusChange.isPending}
                    onClick={() => statusChange.mutate("CANCELLED")}>
                    {t("inbound.actions.cancelAsn")}
                  </Button>
                )}
              </Stack>
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                <Chip data-testid="asn-status"
                  label={operationalStatusLabel(asn.status)}
                  color={statusColors[asn.status]}
                  variant={asn.status === "RECEIVED" ? "outlined" : "filled"} />
                <LabeledInfo label={t("inbound.fields.warehouse")}>{asn.warehouseCode}</LabeledInfo>
                {asn.clientName && (
                  <LabeledInfo label={t("inbound.card.labels.client")}>
                    {asn.clientName}{asn.clientCode ? ` (${asn.clientCode})` : ""}
                  </LabeledInfo>
                )}
                {asn.purchaseOrderNumber && (
                  <LabeledInfo label={t("inbound.fields.purchaseOrder")}>{asn.purchaseOrderNumber}</LabeledInfo>
                )}
                {asn.customsClearanceRequired && (
                  <Chip label={asn.customsClearanceNumber
                    ? `${asn.customsClearanceNumber} · ${t("inbound.labels.customs")}`
                    : t("inbound.labels.customsRequired")} color="warning" variant="outlined" />
                )}
              </Stack>
            </Stack>
            {asn.status === "CUSTOMS_PENDING"
              && asn.customsClearanceStatus === "IN_PROGRESS" && (
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Typography variant="h6" fontWeight={850}>
                      {t("inbound.customs.control", { number: asn.customsClearanceNumber })}
                    </Typography>
                    <FormControlLabel control={<Switch
                      checked={customsInvoicePresent}
                      onChange={(event) => setCustomsInvoicePresent(
                        event.target.checked
                      )} />}
                      label={t("inbound.customs.commercialInvoice", { value:
                        t(customsInvoicePresent ? "inbound.customs.yes" : "inbound.customs.no") })} />
                    <FormControlLabel control={<Switch
                      checked={customsPackingListPresent}
                      onChange={(event) => setCustomsPackingListPresent(
                        event.target.checked
                      )} />}
                      label={t("inbound.customs.packingList", { value:
                        t(customsPackingListPresent ? "inbound.customs.yes" : "inbound.customs.no") })} />
                    <FormControlLabel control={<Switch
                      checked={customsOriginProvided}
                      onChange={(event) => setCustomsOriginProvided(
                        event.target.checked
                      )} />}
                      label={t("inbound.customs.origin", { value:
                        t(customsOriginProvided ? "inbound.customs.yes" : "inbound.customs.no") })} />
                    <TextField required label={t("inbound.fields.customsReference")}
                      value={customsReference}
                      onChange={(event) => setCustomsReference(event.target.value)} />
                    <TextField label={t("inbound.fields.broker")}
                      value={customsBroker}
                      onChange={(event) => setCustomsBroker(event.target.value)} />
                    <FormControlLabel control={<Switch
                      checked={customsPhysicalCheck}
                      onChange={(event) => setCustomsPhysicalCheck(
                        event.target.checked
                      )} />}
                      label={t("inbound.customs.physicalControl", { value:
                        t(customsPhysicalCheck ? "inbound.customs.yes" : "inbound.customs.no") })} />
                    <FormControl fullWidth required>
                      <InputLabel>{t("inbound.fields.decision")}</InputLabel>
                      <Select value={customsDecision} label={t("inbound.fields.decision")}
                        onChange={(event) => setCustomsDecision(event.target.value as
                          typeof customsDecision)}>
                        <MenuItem value="CLEARANCE_GRANTED">
                          {t("inbound.customs.decisions.RELEASED")}
                        </MenuItem>
                        <MenuItem value="ADDITIONAL_INFO_REQUIRED">
                          {t("inbound.customs.decisions.ADDITIONAL_INFO_REQUIRED")}
                        </MenuItem>
                        <MenuItem value="REFUSED">{t("inbound.customs.decisions.REFUSED")}</MenuItem>
                      </Select>
                    </FormControl>
                    <TextField label={t("inbound.fields.commentShort")} multiline minRows={2}
                      value={customsComment}
                      onChange={(event) => setCustomsComment(event.target.value)} />
                    <Button variant="contained"
                      disabled={customsAction.isPending
                        || (customsDecision === "CLEARANCE_GRANTED"
                          && (!customsReference.trim() || !customsInvoicePresent
                            || !customsPackingListPresent
                            || !customsOriginProvided))}
                      onClick={() => customsAction.mutate()}>
                      {t("inbound.actions.validateDecision")}
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            )}
            {statusChange.isError && (
              <Alert severity="error">
                {statusChange.error instanceof Error
                  ? statusChange.error.message
                  : t("inbound.errors.transition")}
              </Alert>
            )}
            {generateAsnDocument.isError && (
              <Alert severity="error">
                {generateAsnDocument.error instanceof Error
                  ? generateAsnDocument.error.message
                  : t("inbound.errors.documentGeneration")}
              </Alert>
            )}

            <Typography variant="h6">{t("inbound.sections.expectedLines")}</Typography>
            {lines.data?.map((line) => (
              <Card key={line.id} variant="outlined">
                <CardContent>
                  <Stack spacing={1}>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <LabeledInfo label={t("inbound.card.labels.line")}>
                      {line.lineNumber}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.fields.item")} prominent>
                      {line.itemCode}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.card.labels.description")} prominent>
                      {line.itemName}
                    </LabeledInfo>
                    </Stack>
                    <Stack data-testid={`asn-line-${line.lineNumber}-quantities`} direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <LabeledInfo label={t("inbound.card.labels.expected")}>
                      {line.expectedQuantity} {line.expectedUom}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.fields.received")}>
                      {line.receivedQuantity} {line.expectedUom}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.card.labels.remaining")}>
                      {line.remainingQuantity} {line.expectedUom}
                    </LabeledInfo>
                    {line.packagingCode && (<LabeledInfo label={t("inbound.fields.packaging")}>
                      {line.packagingCode}
                    </LabeledInfo>)}
                    {line.expectedLotNumber && (<LabeledInfo label={t("inbound.fields.expectedLot")}>
                      {line.expectedLotNumber}
                    </LabeledInfo>)}
                    {line.expectedExpirationAt && (<LabeledInfo label={t("inbound.fields.expectedExpiry")}>
                      {new Date(line.expectedExpirationAt).toLocaleDateString(translation.resolvedLanguage)}
                    </LabeledInfo>)}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}

            {asn.status === "DRAFT" && (
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" fontWeight={700}>
                  {t("inbound.actions.addLine")}
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <FormControl fullWidth required>
                    <InputLabel>{t("inbound.fields.item")}</InputLabel>
                    <Select value={itemId} label={t("inbound.fields.item")}
                      data-testid="asn-line-item"
                      onChange={(event) => setItemId(event.target.value)}>
                      {products.data?.filter((item) => item.status === "ACTIVE")
                        .map((item) => (
                          <MenuItem key={item.id} value={item.id}>
                            {item.code} · {item.displayName}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <FormControl fullWidth>
                    <InputLabel>{t("inbound.fields.packaging")}</InputLabel>
                    <Select value={packagingId} label={t("inbound.fields.packaging")}
                      data-testid="asn-line-packaging"
                      disabled={!itemId}
                      onChange={(event) => {
                        const nextPackaging = packaging.data?.find(
                          (candidate) => candidate.id === event.target.value
                        );
                        setPackagingId(event.target.value);
                        if (nextPackaging) setUom(nextPackaging.uom);
                      }}>
                      <MenuItem value=""><em>{t("inbound.fields.baseUnit")}</em></MenuItem>
                      {packaging.data?.map((pack) => (
                        <MenuItem key={pack.id} value={pack.id}>
                          {pack.code} · {pack.uom} × {pack.baseQuantity}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField type="number" label={t("inbound.fields.quantity")} required value={quantity}
                    inputProps={{ min: 0.000001, step: 0.000001 }}
                    onChange={(event) => setQuantity(event.target.value)} />
                  <TextField label={t("inbound.fields.uom")} required value={uom}
                    onChange={(event) => setUom(event.target.value.toUpperCase())} />
                  <TextField label={t("inbound.fields.expectedLot")} value={lotNumber}
                    onChange={(event) => setLotNumber(event.target.value)} />
                  <TextField type="date" label={t("inbound.fields.expectedExpiry")}
                    value={expirationDate} InputLabelProps={{ shrink: true }}
                    onChange={(event) => setExpirationDate(event.target.value)} />
                </Stack>
                {addLine.isError && (
                  <Alert severity="error">
                    {t("inbound.errors.lineCreate")}
                  </Alert>
                )}
                <Button variant="outlined" startIcon={<AddOutlined />}
                  data-testid="asn-add-line"
                  disabled={addLine.isPending || !itemId || Number(quantity) <= 0}
                  onClick={() => addLine.mutate()}>
                  {t("inbound.actions.addTheLine")}
                </Button>
              </Stack>
            )}

            {["ARRIVED", "RECEIVING", "RECEIVED", "CLOSED"].includes(asn.status) && (
              <Stack spacing={1.5}>
                <Typography variant="h6">{t("inbound.sections.receipts")}</Typography>
                {receipts.data?.map((receipt) => (
                  <Card key={receipt.id} variant="outlined"
                    onClick={() => setActiveReceipt(receipt)}
                    sx={{ cursor: "pointer" }}>
                    <CardContent>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                        alignItems={{ sm: "center" }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography fontWeight={800}>{receipt.receiptNumber}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {t("common.line", { count: receipt.lineCount })}
                            {" · "}{new Date(receipt.startedAt).toLocaleString(translation.resolvedLanguage)}
                          </Typography>
                        </Box>
                        <Chip label={receiptStatusLabel(receipt.status)}
                          color={operationalStatusColor(receipt.status)} />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
                {canStartAsnReceipt(asn, lines.data, receipts.data) && (
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                    alignItems={{ sm: "flex-start" }}>
                    <TextField fullWidth label={t("inbound.fields.receiptNumber")}
                      disabled={numbering.isPending || numberingUnavailable
                        || receiptNumbering?.automatic}
                      value={receiptNumbering?.automatic
                        ? receiptNumbering.preview ?? t("inbound.numbering.automatic")
                        : receiptNumber}
                      helperText={numbering.isPending
                        ? t("inbound.numbering.loading")
                        : numberingUnavailable
                          ? t("inbound.numbering.unavailableAdmin")
                          : receiptNumbering?.automatic
                        ? t("inbound.numbering.previewConcurrent") : t("inbound.numbering.manualRequired")}
                      onChange={(event) => setReceiptNumber(event.target.value.toUpperCase())} />
                    <Button variant="contained" startIcon={<MoveToInboxOutlined />}
                      data-testid="asn-start-receipt"
                      disabled={startReceipt.isPending || numbering.isPending
                        || numberingUnavailable ||
                        (!receiptNumbering?.automatic && !receiptNumber.trim())}
                      onClick={() => startReceipt.mutate()}
                      sx={{
                        height: 56,
                        minWidth: { sm: 180 },
                        flexShrink: 0,
                        alignSelf: { xs: "stretch", sm: "flex-start" }
                      }}>
                      {t("common.actions.start")}
                    </Button>
                  </Stack>
                )}
                {startReceipt.isError && (
                  <Alert severity="error">
                    {(() => {
                      const error = startReceiptError(startReceipt.error);
                      return error.value ?? t(`inbound.errors.${error.code}`);
                    })()}
                  </Alert>
                )}
              </Stack>
            )}

            <Accordion disableGutters elevation={0} variant="outlined">
              <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                <Typography variant="h6">
                  Documents {generatedDocuments.data
                    ? `(${generatedDocuments.data.length})` : ""}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1}>
                  {generatedDocuments.isError && (
                    <Alert severity="error">{t("inbound.errors.documentHistory")}</Alert>
                  )}
                  {openStoredDocument.isError && (
                    <Alert severity="error">{t("inbound.errors.documentOpen")}</Alert>
                  )}
                  {generatedDocuments.data?.length === 0 && (
                    <Typography color="text.secondary">
                      {t("inbound.emptyDetails.generatedDocuments")}
                    </Typography>
                  )}
                  {generatedDocuments.data?.map((document) => (
                    <Card key={document.id} variant="outlined">
                      <CardContent sx={{ py: 1.25, "&:last-child": { pb: 1.25 } }}>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                          alignItems={{ sm: "center" }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography fontWeight={800}>
                              {document.number} · {document.filename}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {document.templateNumber} v{document.templateVersion}
                              {" · "}{new Date(document.generatedAt).toLocaleString(translation.resolvedLanguage)}
                              {" · "}{Math.ceil(document.contentSize / 1024)} Ko
                              {document.generatedBy ? ` · ${document.generatedBy}` : ""}
                            </Typography>
                          </Box>
                          <Button size="small" variant="outlined"
                            startIcon={<VisibilityOutlined />}
                            disabled={openStoredDocument.isPending}
                            onClick={() => openStoredDocument.mutate({
                              id: document.id, filename: document.filename
                            })}>
                            {t("inbound.actions.view")}
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>

            <Accordion disableGutters elevation={0} variant="outlined">
              <AccordionSummary expandIcon={<ExpandMoreOutlined />}>
                <Typography variant="h6">
                  Audit {audit.data ? `(${audit.data.length})` : ""}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={1.5}>
                  {audit.data?.map((entry) => (
                    <Box key={entry.id}>
                      <Typography variant="body2" fontWeight={700}>
                        {entry.entityType} · {entry.action}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(entry.changedAt).toLocaleString(translation.resolvedLanguage)}
                        {" · "}{entry.changedBy}
                        {entry.changedByEmail ? ` · ${entry.changedByEmail}` : ""}
                      </Typography>
                      {entry.reason && (
                        <Typography variant="body2" sx={{ mt: 0.25 }}>
                          {t("inbound.labels.reason", { reason: entry.reason })}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Stack>
              </AccordionDetails>
            </Accordion>
          </Stack>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>{t("common.actions.close")}</Button></DialogActions>
      <ReceiptExecution receipt={activeReceipt} asn={asn} asnLines={lines.data ?? []}
        onClose={() => setActiveReceipt(null)}
        onChanged={async () => {
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ["asn-lines", asn?.id] }),
            queryClient.invalidateQueries({ queryKey: ["receipts", asn?.id] }),
            queryClient.invalidateQueries({ queryKey: ["asns"] }),
            queryClient.invalidateQueries({ queryKey: ["asn-audit", asn?.id] })
          ]);
          onAsnChange(await loadAsn(asn!.id));
        }}
        onCompleted={async (receipt) => {
          setActiveReceipt(receipt);
          onAsnChange(await loadAsn(asn!.id));
        }} />
    </Dialog>
    <Dialog open={Boolean(documentPreview)} onClose={closeDocumentPreview}
      fullScreen>
      <DialogTitle>{documentPreview?.filename}</DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: "grey.200" }}>
        {documentPreview && (
          <Box component="iframe" title={documentPreview.filename}
            src={documentPreview.url}
            sx={{ display: "block", width: "100%", height: "100%", border: 0 }} />
        )}
      </DialogContent>
      <DialogActions>
        {documentPreview && (
          <Button component="a" href={documentPreview.url}
            download={documentPreview.filename} variant="outlined">
            {t("inbound.actions.download")}
          </Button>
        )}
        <Button onClick={closeDocumentPreview} variant="contained">{t("common.actions.close")}</Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

function ReceiptExecution({
  receipt,
  asn,
  asnLines,
  onClose,
  onChanged,
  onCompleted
}: {
  receipt: Receipt | null;
  asn: Asn | null;
  asnLines: AsnLine[];
  onClose: () => void;
  onChanged: () => Promise<void>;
  onCompleted: (receipt: Receipt) => Promise<void>;
}) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [asnLineId, setAsnLineId] = useState("");
  const [itemId, setItemId] = useState("");
  const [itemPackagingId, setItemPackagingId] = useState("");
  const [received, setReceived] = useState("0");
  const [accepted, setAccepted] = useState("0");
  const [rejected, setRejected] = useState("0");
  const [quarantined, setQuarantined] = useState("0");
  const [lotNumber, setLotNumber] = useState("");
  const [expirationDate, setExpirationDate] = useState("");
  const [acceptedSerials, setAcceptedSerials] = useState("");
  const [rejectedSerials, setRejectedSerials] = useState("");
  const [quarantinedSerials, setQuarantinedSerials] = useState("");
  const [correctionReason, setCorrectionReason] = useState("");
  const [correctionMode, setCorrectionMode] = useState(false);
  const [correctionDialogOpen, setCorrectionDialogOpen] = useState(false);
  const [acceptedPointId, setAcceptedPointId] = useState("");
  const [quarantinePointId, setQuarantinePointId] = useState("");
  const [rejectedPointId, setRejectedPointId] = useState("");
  const selectedLine = asnLines.find((line) => line.id === asnLineId);
  const isAdHoc = receipt?.sourceType === "AD_HOC";
  const products = useQuery({
    queryKey: ["products"],
    queryFn: loadProducts,
    enabled: Boolean(receipt && isAdHoc)
  });
  const selectedProduct = products.data?.find((item) => item.id === itemId);
  const packaging = useQuery({
    queryKey: ["packaging", itemId],
    queryFn: () => loadPackaging(itemId),
    enabled: Boolean(receipt && isAdHoc && itemId)
  });
  const selectedPackaging = packaging.data?.find(
    (option) => option.id === itemPackagingId
  );
  const trackingItem = selectedLine ?? selectedProduct;
  const receiptLines = useQuery({
    queryKey: ["receipt-lines", receipt?.id],
    queryFn: () => loadReceiptLines(receipt!.id),
    enabled: Boolean(receipt)
  });
  const processPoints = useQuery({
    queryKey: ["process-points", receipt?.warehouseId],
    queryFn: () => loadProcessPoints(receipt!.warehouseId),
    enabled: Boolean(receipt
      && ["POSTING", "COMPLETED"].includes(receipt.status))
  });
  const stock = useQuery({
    queryKey: ["receipt-stock", receipt?.id],
    queryFn: () => loadReceiptStock(receipt!.id),
    enabled: Boolean(receipt)
  });

  useEffect(() => {
    setCorrectionMode(false);
    setCorrectionDialogOpen(false);
    setCorrectionReason("");
  }, [receipt?.id]);

  useEffect(() => {
    const points = processPoints.data ?? [];
    setAcceptedPointId((current) => current || points.find(
      (point) => point.defaultForReceiving && point.status === "ACTIVE"
    )?.id || points.find(
      (point) => ["RECEIVING", "STAGING_AREA"].includes(point.type)
        && point.status === "ACTIVE" && point.putawaySourceEnabled
    )?.id || "");
    setQuarantinePointId((current) => current || points.find(
      (point) => point.type === "QUARANTINE" && point.status === "ACTIVE"
    )?.id || "");
    setRejectedPointId((current) => current || points.find(
      (point) => ["QUALITY_CONTROL", "QUARANTINE"].includes(point.type)
        && point.status === "ACTIVE"
    )?.id || "");
  }, [processPoints.data]);

  useEffect(() => {
    if (!selectedLine) return;
    const remaining = String(selectedLine.remainingQuantity);
    setReceived(remaining);
    setAccepted(remaining);
    setRejected("0");
    setQuarantined("0");
    setLotNumber(selectedLine.expectedLotNumber ?? "");
    setExpirationDate(selectedLine.expectedExpirationAt ?? "");
    setAcceptedSerials("");
    setRejectedSerials("");
    setQuarantinedSerials("");
  }, [selectedLine]);

  const parseSerials = (value: string) => value
    .split(/[\n,;]+/)
    .map((serial) => serial.trim())
    .filter(Boolean);
  const acceptedSerialNumbers = parseSerials(acceptedSerials);
  const rejectedSerialNumbers = parseSerials(rejectedSerials);
  const quarantinedSerialNumbers = parseSerials(quarantinedSerials);
  const serialsValid = !trackingItem?.serialTracking || (
    Number.isInteger(Number(received)) &&
    Number.isInteger(Number(accepted)) &&
    Number.isInteger(Number(rejected)) &&
    Number.isInteger(Number(quarantined)) &&
    acceptedSerialNumbers.length === Number(accepted) &&
    rejectedSerialNumbers.length === Number(rejected) &&
    quarantinedSerialNumbers.length === Number(quarantined) &&
    new Set([
      ...acceptedSerialNumbers,
      ...rejectedSerialNumbers,
      ...quarantinedSerialNumbers
    ]).size === Number(received)
  );
  const classifiedQuantity =
    Number(accepted) + Number(rejected) + Number(quarantined);
  const quantitiesValid = Number(received) > 0 &&
    Math.abs(classifiedQuantity - Number(received)) < 0.000001 &&
    (isAdHoc || Number(received) <= Number(selectedLine?.remainingQuantity ?? 0));

  const receiveLine = useMutation({
    mutationFn: () => addReceiptLine(receipt!.id, {
      asnLineId: isAdHoc ? null : asnLineId,
      itemId: isAdHoc ? itemId : null,
      itemPackagingId: isAdHoc ? itemPackagingId || null : null,
      receivedQuantity: Number(received),
      acceptedQuantity: Number(accepted),
      rejectedQuantity: Number(rejected),
      quarantinedQuantity: Number(quarantined),
      actualUom: isAdHoc
        ? selectedPackaging?.uom ?? selectedProduct!.baseUom
        : selectedLine!.expectedUom,
      lotNumber: lotNumber || null,
      expirationAt: expirationDate || null,
      acceptedSerialNumbers,
      rejectedSerialNumbers,
      quarantinedSerialNumbers
    }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["receipt-lines", receipt?.id]
      });
      await onChanged();
      setAsnLineId("");
      setItemId("");
      setItemPackagingId("");
      setReceived("0");
      setAccepted("0");
      setRejected("0");
      setQuarantined("0");
      setLotNumber("");
      setExpirationDate("");
      setAcceptedSerials("");
      setRejectedSerials("");
      setQuarantinedSerials("");
    }
  });
  const finishReceipt = useMutation({
    mutationFn: () => completeReceipt(receipt!.id, receipt!.version),
    onSuccess: async (completed) => {
      setCorrectionMode(false);
      setCorrectionReason("");
      await onChanged();
      await onCompleted(completed);
    }
  });
  const reopen = useMutation({
    mutationFn: () => reopenReceipt(
      receipt!.id, receipt!.version, correctionReason
    ),
    onSuccess: async (updated) => {
      setCorrectionDialogOpen(false);
      setCorrectionMode(true);
      await onChanged();
      await onCompleted(updated);
    }
  });
  const removeLine = useMutation({
    mutationFn: (receiptLineId: string) => deleteReceiptLine(
      receipt!.id, receiptLineId, receipt!.version, correctionReason
    ),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({
        queryKey: ["receipt-lines", receipt?.id]
      });
      await onChanged();
      await onCompleted(updated);
    }
  });
  const generateStock = useMutation({
    mutationFn: () => generateReceiptStock(receipt!.id, {
      acceptedProcessPointId: acceptedPointId || null,
      quarantineProcessPointId: quarantinePointId || null,
      rejectedProcessPointId: rejectedPointId || null
    }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["receipt-stock", receipt?.id] }),
        queryClient.invalidateQueries({ queryKey: ["numbering-policies"] })
      ]);
      await onChanged();
      onClose();
    }
  });
  const acceptedTotal = receiptLines.data?.reduce(
    (total, line) => total + Number(line.acceptedQuantity), 0
  ) ?? 0;
  const quarantineTotal = receiptLines.data?.reduce(
    (total, line) => total + Number(line.quarantinedQuantity), 0
  ) ?? 0;
  const rejectedTotal = receiptLines.data?.reduce(
    (total, line) => total + Number(line.rejectedQuantity), 0
  ) ?? 0;
  const activeReceivingPoints = processPoints.data?.filter((point) =>
    ["RECEIVING", "STAGING_AREA"].includes(point.type)
      && point.status === "ACTIVE"
  ) ?? [];
  const stockPointsValid =
    (acceptedTotal === 0 || Boolean(acceptedPointId)) &&
    (quarantineTotal === 0 || Boolean(quarantinePointId)) &&
    (rejectedTotal === 0 || Boolean(rejectedPointId));

  return (
    <Dialog open={Boolean(receipt)} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {t("inbound.receipt.title", { number: receipt?.receiptNumber })}
        {isAdHoc ? " · Ad hoc" : ` · ASN ${asn?.asnNumber}`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={1}>
            <Chip data-testid="receipt-status"
              label={receipt ? receiptStatusLabel(receipt.status) : ""} color={
              operationalStatusColor(receipt?.status)
            } />
            <LabeledInfo label={t("inbound.card.labels.lines")}>{receipt?.lineCount ?? 0}</LabeledInfo>
          </Stack>

          <Typography variant="h6">{t("inbound.sections.recordedMovements")}</Typography>
          {receiptLines.data?.length === 0 && (
            <Alert severity="info">{t("inbound.quantities.noneReceived")}</Alert>
          )}
          {receiptLines.data?.map((line) => (
            <Card key={line.id} variant="outlined">
              <CardContent>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                  alignItems={{ sm: "center" }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Stack spacing={1}>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <LabeledInfo label={t("inbound.card.labels.line")}>
                      {line.lineNumber}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.fields.item")} prominent>
                      {line.itemCode}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.card.labels.description")} prominent>
                      {line.itemName}
                    </LabeledInfo>
                    </Stack>
                    <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                    <LabeledInfo label={t("inbound.fields.received")}>
                      {line.receivedQuantity} {line.actualUom}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.fields.accepted")}>
                      {line.acceptedQuantity} {line.actualUom}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.fields.rejected")}>
                      {line.rejectedQuantity} {line.actualUom}
                    </LabeledInfo>
                    <LabeledInfo label={t("inbound.fields.quarantine")}>
                      {line.quarantinedQuantity} {line.actualUom}
                    </LabeledInfo>
                    {line.lotNumber && (<LabeledInfo label={t("inbound.fields.lot")}>
                      {line.lotNumber}
                    </LabeledInfo>)}
                    </Stack>
                  </Stack>
                  </Box>
                  {receipt?.status === "OPEN" && correctionMode && (
                    <Button color="error" startIcon={<DeleteOutlineOutlined />}
                      data-testid="receipt-delete-line"
                      disabled={removeLine.isPending}
                      onClick={() => removeLine.mutate(line.id)}>
                      {t("inbound.receipt.delete")}
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          ))}

          {correctionMode && receipt?.status === "OPEN" && (
            <Alert severity="info">
              {t("inbound.receipt.correctionMode", { reason: correctionReason })}
            </Alert>
          )}

          {removeLine.isError && (
            <Alert severity="error">
              {removeLine.error instanceof Error
                ? removeLine.error.message : t("inbound.receipt.deleteError")}
            </Alert>
          )}

          {receipt?.status === "OPEN" && (
            <>
              <Typography variant="h6">{t("inbound.sections.receiveQuantity")}</Typography>
              {isAdHoc ? (
                <>
                  <FormControl fullWidth>
                    <InputLabel>{t("inbound.fields.item")}</InputLabel>
                    <Select value={itemId} label={t("inbound.fields.item")}
                      onChange={(event) => {
                        setItemId(event.target.value);
                        setItemPackagingId("");
                      }}>
                      {products.data?.filter((item) =>
                        item.status === "ACTIVE"
                      ).map((item) => (
                        <MenuItem key={item.id} value={item.id}>
                          {item.code} · {item.displayName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {selectedProduct && (
                    <FormControl fullWidth>
                      <InputLabel>{t("inbound.fields.packaging")}</InputLabel>
                      <Select value={itemPackagingId}
                        label={t("inbound.fields.packaging")}
                        onChange={(event) =>
                          setItemPackagingId(event.target.value)}>
                        <MenuItem value="">
                          {t("inbound.receipt.unknownPackaging")}
                        </MenuItem>
                        {packaging.data?.filter((option) =>
                          option.status === "ACTIVE"
                        ).map((option) => (
                          <MenuItem key={option.id} value={option.id}>
                            {option.code} · {option.displayName}
                            {" · "}{option.uom}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </>
              ) : (
                <FormControl fullWidth>
                  <InputLabel>{t("inbound.receipt.asnLine")}</InputLabel>
                  <Select value={asnLineId} label={t("inbound.receipt.asnLine")}
                    data-testid="receipt-asn-line"
                    onChange={(event) => setAsnLineId(event.target.value)}>
                    {asnLines.filter((line) =>
                      Number(line.remainingQuantity) > 0
                    ).map((line) => (
                      <MenuItem key={line.id} value={line.id}>
                        {line.lineNumber} · {line.itemCode} · {t("inbound.receipt.remaining")} {" "}
                        {line.remainingQuantity} {line.expectedUom}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {selectedLine && (
                <Alert severity={Number(received) < selectedLine.remainingQuantity
                  ? "info" : "success"}>
                  {t("inbound.quantities.expectedAlready", { expected: selectedLine.expectedQuantity,
                    uom: selectedLine.expectedUom, received: selectedLine.receivedQuantity })}
                  {` · ${t("inbound.receipt.remaining")} ${selectedLine.remainingQuantity}`}
                </Alert>
              )}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField type="number" label={t("inbound.fields.received")} value={received}
                  data-testid="receipt-received-quantity"
                  inputProps={{ min: 0, step: 0.000001 }}
                  onChange={(event) => {
                    setReceived(event.target.value);
                    setAccepted(event.target.value);
                  }} />
                <TextField type="number" label={t("inbound.fields.accepted")} value={accepted}
                  data-testid="receipt-accepted-quantity"
                  inputProps={{ min: 0, step: 0.000001 }}
                  onChange={(event) => setAccepted(event.target.value)} />
                <TextField type="number" label={t("inbound.fields.rejected")} value={rejected}
                  data-testid="receipt-rejected-quantity"
                  inputProps={{ min: 0, step: 0.000001 }}
                  onChange={(event) => setRejected(event.target.value)} />
                <TextField type="number" label={t("inbound.fields.quarantine")} value={quarantined}
                  data-testid="receipt-quarantined-quantity"
                  inputProps={{ min: 0, step: 0.000001 }}
                  onChange={(event) => setQuarantined(event.target.value)} />
              </Stack>
              {trackingItem?.serialTracking && (
                <Stack spacing={1}>
                  <Alert severity="info">
                    {t("inbound.receipt.serializedHelp")}
                  </Alert>
                  {Number(accepted) > 0 && (
                    <TextField multiline minRows={2}
                      label={t("inbound.receipt.serialAccepted", { current: acceptedSerialNumbers.length, expected: Number(accepted) })}
                      data-testid="receipt-accepted-serials"
                      value={acceptedSerials}
                      onChange={(event) => setAcceptedSerials(event.target.value)} />
                  )}
                  {Number(rejected) > 0 && (
                    <TextField multiline minRows={2}
                      label={t("inbound.receipt.serialRejected", { current: rejectedSerialNumbers.length, expected: Number(rejected) })}
                      value={rejectedSerials}
                      onChange={(event) => setRejectedSerials(event.target.value)} />
                  )}
                  {Number(quarantined) > 0 && (
                    <TextField multiline minRows={2}
                      label={t("inbound.receipt.serialQuarantined", { current: quarantinedSerialNumbers.length, expected: Number(quarantined) })}
                      data-testid="receipt-quarantined-serials"
                      value={quarantinedSerials}
                      onChange={(event) => setQuarantinedSerials(event.target.value)} />
                  )}
                  {!serialsValid && (
                    <Alert severity="warning">
                      {t("inbound.receipt.serialMismatch")}
                    </Alert>
                  )}
                </Stack>
              )}
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                <TextField fullWidth label={t("inbound.fields.lot")} value={lotNumber}
                  onChange={(event) => setLotNumber(event.target.value)} />
                <TextField fullWidth type="date" label={t("inbound.fields.expiry")}
                  value={expirationDate} InputLabelProps={{ shrink: true }}
                  onChange={(event) => setExpirationDate(event.target.value)} />
              </Stack>
              {!quantitiesValid && (asnLineId || itemId) && (
                <Alert severity="warning">
                  {t("inbound.receipt.quantityMismatch", { limit: isAdHoc ? "" : t("inbound.receipt.withoutExceeding") })}
                </Alert>
              )}
              {receiveLine.isError && (
                <Alert severity="error">
                  {receiveLine.error instanceof Error
                    ? receiveLine.error.message
                    : t("inbound.receipt.saveError")}
                </Alert>
              )}
              <Button variant="outlined" startIcon={<AddOutlined />}
                data-testid="receipt-record-quantity"
                disabled={!(isAdHoc ? itemId : asnLineId)
                  || !quantitiesValid || !serialsValid
                  || receiveLine.isPending}
                onClick={() => receiveLine.mutate()}>
                {t("inbound.actions.saveQuantity")}
              </Button>
            </>
          )}

          {receipt && ["POSTING", "COMPLETED"].includes(receipt.status) && (
            <>
              {reopen.isError && (
                <Alert severity="error">
                  {reopen.error instanceof Error
                    ? reopen.error.message : t("inbound.errors.reopen")}
                </Alert>
              )}
              <Typography variant="h6">{t("inbound.sections.stockUnits")}</Typography>
              {(stock.data?.length ?? 0) === 0 ? (
                <Stack spacing={1.5}>
                  <Alert severity="info">
                    {t("inbound.receipt.stockPending")}
                  </Alert>
                  {acceptedTotal > 0 && !processPoints.isLoading
                    && activeReceivingPoints.length === 0 && (
                    <Alert severity="warning">
                      {t("inbound.receipt.noReceivingPoint")}
                    </Alert>
                  )}
                  {acceptedTotal > 0 && (
                    <FormControl fullWidth>
                      <InputLabel>{t("inbound.receipt.acceptedPoint")}</InputLabel>
                      <Select value={acceptedPointId}
                        label={t("inbound.receipt.acceptedPoint")}
                        onChange={(event) => setAcceptedPointId(event.target.value)}>
                        {activeReceivingPoints.map((point) => (
                          <MenuItem key={point.id} value={point.id}>
                            {point.code} · {point.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {quarantineTotal > 0 && (
                    <FormControl fullWidth>
                      <InputLabel>{t("inbound.receipt.quarantinePoint")}</InputLabel>
                      <Select value={quarantinePointId} label={t("inbound.receipt.quarantinePoint")}
                        data-testid="receipt-quarantine-point"
                        onChange={(event) => setQuarantinePointId(event.target.value)}>
                        {processPoints.data?.filter((point) =>
                          point.type === "QUARANTINE" && point.status === "ACTIVE"
                        ).map((point) => (
                          <MenuItem key={point.id} value={point.id}>
                            {point.code} · {point.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {rejectedTotal > 0 && (
                    <FormControl fullWidth>
                      <InputLabel>{t("inbound.receipt.rejectedPoint")}</InputLabel>
                      <Select value={rejectedPointId} label={t("inbound.receipt.rejectedPoint")}
                        onChange={(event) => setRejectedPointId(event.target.value)}>
                        {processPoints.data?.filter((point) =>
                          ["QUALITY_CONTROL", "QUARANTINE"].includes(point.type)
                          && point.status === "ACTIVE"
                        ).map((point) => (
                          <MenuItem key={point.id} value={point.id}>
                            {point.code} · {point.displayName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                  {generateStock.isError && (
                    <Alert severity="error">
                      {generateStock.error instanceof Error
                        ? generateStock.error.message
                        : t("inbound.errors.stockCreate")}
                    </Alert>
                  )}
                  <Button variant="contained"
                    data-testid="receipt-post-stock"
                    disabled={!stockPointsValid || generateStock.isPending}
                    onClick={() => generateStock.mutate()}>
                    {t("inbound.actions.createStock")}
                  </Button>
                </Stack>
              ) : (
                <Stack spacing={1}>
                  {stock.data?.map((hu) => (
                    <Card key={hu.id} variant="outlined">
                      <CardContent>
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}
                          alignItems={{ sm: "center" }}>
                          <Box sx={{ flexGrow: 1 }}>
                            <Typography fontWeight={800}>
                              {hu.huNumber} · {hu.itemCode}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {hu.quantity} {hu.uom} · {hu.processPointCode}
                              {hu.lotNumber ? ` · lot ${hu.lotNumber}` : ""}
                            </Typography>
                          </Box>
                          <Chip label={hu.qualityStatus}
                            color={hu.qualityStatus === "AVAILABLE"
                              ? "success"
                              : hu.qualityStatus === "QUARANTINED"
                                ? "warning" : "error"} />
                          <Chip label={operationalStatusLabel(hu.status)}
                            color={operationalStatusColor(hu.status)} />
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}
                </Stack>
              )}
            </>
          )}
          {finishReceipt.isError && (
            <Alert severity="error">
              {finishReceipt.error instanceof Error
                ? finishReceipt.error.message
                : t("inbound.errors.receiptComplete")}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <Dialog open={correctionDialogOpen}
        onClose={() => !reopen.isPending && setCorrectionDialogOpen(false)}
        fullWidth maxWidth="sm">
        <DialogTitle>
          {receipt?.status !== "OPEN"
            ? t("inbound.actions.reopenForCorrection")
            : t("inbound.receipt.activateCorrection")}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Alert severity="info">
              {t("inbound.receipt.auditReason")}
            </Alert>
            <TextField autoFocus fullWidth required multiline minRows={3}
              label={t("inbound.fields.correctionReason")}
              data-testid="receipt-correction-reason"
              value={correctionReason}
              helperText={t("inbound.receipt.reasonHelper")}
              onChange={(event) => setCorrectionReason(event.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button disabled={reopen.isPending}
            onClick={() => setCorrectionDialogOpen(false)}>
            {t("common.actions.cancel")}
          </Button>
          <Button variant="contained" startIcon={<UndoOutlined />}
            data-testid="receipt-confirm-correction"
            disabled={correctionReason.trim().length < 3 || reopen.isPending}
            onClick={() => {
              if (receipt?.status !== "OPEN") {
                reopen.mutate();
              } else {
                setCorrectionMode(true);
                setCorrectionDialogOpen(false);
              }
            }}>
            {receipt?.status !== "OPEN"
              ? t("inbound.actions.reopen") : t("inbound.receipt.correct")}
          </Button>
        </DialogActions>
      </Dialog>
      <DialogActions>
        <Button onClick={onClose}>{t("common.actions.close")}</Button>
        {receipt?.status === "OPEN" && (receiptLines.data?.length ?? 0) > 0 && (
          <Button variant="outlined" startIcon={<UndoOutlined />}
            data-testid="receipt-correct"
            onClick={() => {
              if (correctionMode) {
                setCorrectionMode(false);
                setCorrectionReason("");
              } else {
                setCorrectionDialogOpen(true);
              }
            }}>
            {correctionMode ? t("inbound.receipt.leaveCorrection") : t("inbound.receipt.correct")}
          </Button>
        )}
        {receipt && ["POSTING", "COMPLETED"].includes(receipt.status)
          && !stock.isLoading && (stock.data?.length ?? 0) === 0 && (
          <Button variant="outlined" startIcon={<UndoOutlined />}
            data-testid="receipt-correct"
            onClick={() => setCorrectionDialogOpen(true)}>
            {t("inbound.receipt.correct")}
          </Button>
        )}
        {receipt?.status === "OPEN" && (
          <Button variant="contained" data-testid="receipt-complete" disabled={
            finishReceipt.isPending || (receiptLines.data?.length ?? 0) === 0
          } onClick={() => finishReceipt.mutate()}>
            {t("inbound.actions.finishReceipt")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
